/**
 * DNACORE V4 — THE OPERATING SYSTEM FOR EDUCATION
 * Design System · Primitivas reutilizáveis (Onda 1)
 *
 * 100% aditivo — não substitui componentes existentes. Páginas migram quando o time decidir.
 *
 * Uso:
 *   import { DnaCard, DnaButton, DnaBadge, DnaInput, DnaModal, DnaDrawer, DnaSkeleton, DnaEmptyState, DnaHelixBg } from '@/design-system/DnaDesignSystem';
 */
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import './DnaDesignSystem.css';

// ─────────── DnaCard ───────────
export function DnaCard({ children, interactive = false, glass = false, accent = false, className = '', testId, onClick, title, ...rest }) {
  const cls = ['dna4-card', interactive && 'interactive', glass && 'glass', accent && 'accent', className].filter(Boolean).join(' ');
  return (
    <div className={cls} data-testid={testId} onClick={onClick} {...rest}>
      {title && <div className="dna4-card-title">{title}</div>}
      {children}
    </div>
  );
}

// ─────────── DnaButton ───────────
export function DnaButton({
  children,
  variant = 'primary',        // primary | secondary | ghost | success | danger
  size = 'md',                // sm | md | lg
  pill = false,
  icon: Icon = null,
  iconRight: IconRight = null,
  disabled = false,
  onClick,
  type = 'button',
  className = '',
  testId,
  ...rest
}) {
  const cls = ['dna4-btn', variant, size === 'sm' && 'small', size === 'lg' && 'large', pill && 'pill', className].filter(Boolean).join(' ');
  return (
    <button type={type} className={cls} disabled={disabled} onClick={onClick} data-testid={testId} {...rest}>
      {Icon && <Icon className="w-4 h-4" />}
      {children}
      {IconRight && <IconRight className="w-4 h-4" />}
    </button>
  );
}

// ─────────── DnaBadge ───────────
export function DnaBadge({ children, variant = 'default', icon: Icon = null, className = '', testId }) {
  const cls = ['dna4-badge', variant !== 'default' && variant, className].filter(Boolean).join(' ');
  return (
    <span className={cls} data-testid={testId}>
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
}

// ─────────── DnaInput / DnaTextarea / DnaSelect ───────────
export function DnaInput({ label, error, className = '', testId, ...rest }) {
  return (
    <label className="block" style={{ marginBottom: 12 }}>
      {label && <span className="dna4-label">{label}</span>}
      <input className={`dna4-input ${className}`} data-testid={testId} {...rest} />
      {error && <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: 'var(--dna-danger)' }}>{error}</span>}
    </label>
  );
}

export function DnaTextarea({ label, error, className = '', testId, ...rest }) {
  return (
    <label className="block" style={{ marginBottom: 12 }}>
      {label && <span className="dna4-label">{label}</span>}
      <textarea className={`dna4-textarea ${className}`} data-testid={testId} {...rest} />
      {error && <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: 'var(--dna-danger)' }}>{error}</span>}
    </label>
  );
}

export function DnaSelect({ label, options = [], className = '', testId, ...rest }) {
  return (
    <label className="block" style={{ marginBottom: 12 }}>
      {label && <span className="dna4-label">{label}</span>}
      <select className={`dna4-select ${className}`} data-testid={testId} {...rest}>
        {options.map((o) => (
          typeof o === 'string'
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

// ─────────── DnaModal ───────────
export function DnaModal({ open, onClose, title, subtitle, footer, children, maxWidth = 560, testId = 'dna4-modal' }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="dna4-modal-overlay" onClick={onClose} data-testid={`${testId}-overlay`}>
      <div className="dna4-modal" style={{ maxWidth }} onClick={(e) => e.stopPropagation()} data-testid={testId}>
        {(title || subtitle) && (
          <div className="dna4-modal-head">
            <div>
              {title && <div className="dna4-modal-title">{title}</div>}
              {subtitle && <div className="dna4-modal-sub">{subtitle}</div>}
            </div>
            <button className="dna4-modal-close" onClick={onClose} aria-label="Fechar" data-testid={`${testId}-close`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="dna4-modal-body">{children}</div>
        {footer && <div className="dna4-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// ─────────── DnaDrawer ───────────
export function DnaDrawer({ open, onClose, title, children, width = 440, testId = 'dna4-drawer' }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="dna4-drawer-overlay" onClick={onClose} data-testid={`${testId}-overlay`} />
      <aside className="dna4-drawer" style={{ width }} data-testid={testId}>
        <div className="dna4-drawer-head">
          {title && <div className="dna4-modal-title">{title}</div>}
          <button className="dna4-modal-close" onClick={onClose} aria-label="Fechar" data-testid={`${testId}-close`}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="dna4-drawer-body">{children}</div>
      </aside>
    </>
  );
}

// ─────────── DnaSkeleton ───────────
export function DnaSkeleton({ width = '100%', height = 16, radius, style = {}, className = '' }) {
  const s = { width, height, borderRadius: radius, ...style };
  return <div className={`dna4-skel ${className}`} style={s} />;
}

// ─────────── DnaEmptyState ───────────
export function DnaEmptyState({ icon: Icon, title, sub, action, testId = 'dna4-empty' }) {
  return (
    <div className="dna4-empty" data-testid={testId}>
      {Icon && <div className="dna4-empty-ico"><Icon className="w-7 h-7" /></div>}
      {title && <div className="dna4-empty-title">{title}</div>}
      {sub && <div className="dna4-empty-sub">{sub}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

// ─────────── DnaHelixBg / DnaNeuralBg ───────────
export function DnaHelixBg({ children, className = '' }) {
  return <div className={`dna4-helix-bg ${className}`}>{children}</div>;
}
export function DnaNeuralBg({ children, className = '' }) {
  return <div className={`dna4-neural-bg ${className}`}>{children}</div>;
}

// ─────────── DnaTableV4 (wrapper premium para tabelas legacy) ───────────
/**
 * Wrapper que aplica o look V4 a qualquer <table> existente sem reescrever a lógica.
 *
 * Props:
 *   density    : 'compact' | 'comfortable' (default 'comfortable')
 *   stickyHeader : bool — fixa o <thead> ao topo do container scroll
 *   zebra      : bool — adiciona linhas alternadas
 *   theme      : 'auto' | 'light' | 'dark' (default 'auto' — herda do contexto)
 *   title      : string opcional — título no cabeçalho do wrapper
 *   subtitle   : string opcional
 *   actions    : ReactNode — toolbar à direita (botões, filtros)
 *   maxHeight  : string — ex. '65vh' (ativa scroll interno)
 *   children   : <table>...</table>
 *
 * Uso:
 *   <DnaTableV4 stickyHeader maxHeight="65vh" title="Títulos" actions={<DnaButton>...</DnaButton>}>
 *     <table>...</table>
 *   </DnaTableV4>
 */
export function DnaTableV4({
  children,
  density = 'comfortable',
  stickyHeader = false,
  zebra = false,
  theme = 'auto',
  title,
  subtitle,
  actions,
  maxHeight,
  className = '',
  testId = 'dna4-table',
}) {
  const cls = [
    'dna-table-v4',
    `density-${density}`,
    stickyHeader && 'sticky-header',
    zebra && 'zebra',
    theme !== 'auto' && `theme-${theme}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} data-testid={testId}>
      {(title || actions) && (
        <div className="dna-table-v4-toolbar">
          <div>
            {title && <div className="dna-table-v4-title">{title}</div>}
            {subtitle && <div className="dna-table-v4-sub">{subtitle}</div>}
          </div>
          {actions && <div className="dna-table-v4-actions">{actions}</div>}
        </div>
      )}
      <div className="dna-table-v4-scroll" style={maxHeight ? { maxHeight } : undefined}>
        {children}
      </div>
    </div>
  );
}

// ─────────── DnaSection (header de seção padronizado) ───────────
export function DnaSection({ title, sub, action, children, className = '' }) {
  return (
    <section className={className} style={{ marginBottom: 24 }}>
      {(title || action) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            {title && <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--dna-text)' }}>{title}</h2>}
            {sub && <p style={{ fontSize: 13, color: 'var(--dna-text-muted)', marginTop: 2 }}>{sub}</p>}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// Export único de tokens (caso queira programaticamente)
export const DnaTokens = {
  accent: 'var(--dna-accent)',
  accentGold: 'var(--dna-accent-2)',
  success: 'var(--dna-success)',
  warn: 'var(--dna-warn)',
  danger: 'var(--dna-danger)',
  info: 'var(--dna-info)',
  bg: 'var(--dna-bg)',
  text: 'var(--dna-text)',
  muted: 'var(--dna-text-muted)',
  motion: 'var(--dna-motion)',
  ease: 'var(--dna-ease)',
};
